import React, { useState, useEffect } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Button,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { resultsService } from '../api/resultsService';

const ResultsTable = () => {
  const [results, setResults] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    // Load results from the resultsService
    loadResults();
  }, []);

  const loadResults = () => {
    // Getting results automatically sorted by question number using the service
    const sortedResults = resultsService.getAllResults();
    setResults(sortedResults);
  };

  const handleExportCSV = () => {
    // Create CSV content
    const headers = ['Question No.', 'Subject', 'Question', 'Gemini Answer', 'Anthropic Answer', 'OpenAI Answer', 'Actual Correct Answer'];
    const csvContent = results.map(result => [
      result.questionNumber,
      result.subject,
      result.question,
      result.geminiAnswer || '-',
      result.anthropicAnswer || '-',
      result.openaiAnswer || '-',
      result.actualCorrectAnswer || '-'
    ]);

    // Add headers to CSV content
    const csvData = [headers, ...csvContent];

    // Convert to CSV string
    const csvString = csvData
      .map(row => row
        .map(cell => {
          const cellStr = String(cell || ''); // Convert to string, handle null/undefined
          // Escape quotes and wrap in quotes if contains comma or needs escaping
          const escaped = cellStr.replace(/"/g, '""');
          return cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n') 
            ? `"${escaped}"` 
            : escaped;
        })
        .join(',')
      )
      .join('\n');

    // Create blob and download
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `neet_results_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSaveActualAnswer = (questionId) => {
    resultsService.updateActualAnswer(questionId, editValue);
    loadResults(); // Reload data
    setEditingId(null);
    setEditValue('');
  };

  const handleEdit = (result) => {
    setEditingId(result.id);
    setEditValue(result.actualCorrectAnswer || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleDeleteResult = (resultId) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      resultsService.deleteResult(resultId);
      loadResults(); // Reload data
    }
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all results? This cannot be undone.')) {
      resultsService.clearAllResults();
      loadResults(); // Reload data
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          AI Model Answer Comparison
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {results.length > 0 && (
            <>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={handleExportCSV}
                startIcon={<DownloadIcon />}
              >
                Export CSV
              </Button>
              <Button 
                variant="outlined" 
                color="error" 
                onClick={handleClearAll}
              >
                Clear All Results
              </Button>
            </>
          )}
        </Box>
      </Box>
      
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="AI answers comparison table">
          <TableHead>
            <TableRow>
              <TableCell>Q. No</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Question</TableCell>
              <TableCell>Gemini's Answer</TableCell>
              <TableCell>Anthropic's Answer</TableCell>
              <TableCell>OpenAI's Answer</TableCell>
              <TableCell>Actual Correct Answer</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No data available. Process questions to populate this table.
                </TableCell>
              </TableRow>
            ) : (
              results.map((result) => (
                <TableRow key={result.id}>
                  <TableCell>{result.questionNumber}</TableCell>
                  <TableCell>{result.subject}</TableCell>
                  <TableCell>{result.question}</TableCell>
                  <TableCell>{result.geminiAnswer || '-'}</TableCell>
                  <TableCell>{result.anthropicAnswer || '-'}</TableCell>
                  <TableCell>{result.openaiAnswer || '-'}</TableCell>
                  <TableCell>
                    {editingId === result.id ? (
                      <FormControl fullWidth size="small">
                        <Select
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                        >
                          <MenuItem value="A">A</MenuItem>
                          <MenuItem value="B">B</MenuItem>
                          <MenuItem value="C">C</MenuItem>
                          <MenuItem value="D">D</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      result.actualCorrectAnswer || '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {editingId === result.id ? (
                      <Box>
                        <Button 
                          size="small" 
                          variant="contained" 
                          onClick={() => handleSaveActualAnswer(result.id)}
                          sx={{ mr: 1 }}
                        >
                          Save
                        </Button>
                        <Button 
                          size="small" 
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex' }}>
                        <Button 
                          size="small" 
                          onClick={() => handleEdit(result)}
                          sx={{ mr: 1 }}
                        >
                          Edit
                        </Button>
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleDeleteResult(result.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ResultsTable;